import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { Text } from './AppText';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '../theme';
import { PressableScale, useReducedMotion } from './Motion';

type FeedbackTone = 'success' | 'error' | 'info';

const TONE = {
  success: { icon: 'checkmark-circle-outline' as const, color: Colors.incomeDk, background: Colors.incomeLt },
  error: { icon: 'alert-circle-outline' as const, color: Colors.expenseDk, background: Colors.expenseLt },
  info: { icon: 'information-circle-outline' as const, color: Colors.primary, background: Colors.primaryLt },
};

export function ActionToast({ message, tone = 'success', style }: { message: string; tone?: FeedbackTone; style?: StyleProp<ViewStyle> }) {
  const palette = TONE[tone];
  const progress = useRef(new Animated.Value(0)).current;
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    progress.stopAnimation();
    if (reducedMotion) {
      progress.setValue(1);
      return;
    }

    progress.setValue(0);
    const animation = Animated.timing(progress, { toValue: 1, duration: 240, easing: Easing.out(Easing.cubic), useNativeDriver: Platform.OS !== 'web' });
    animation.start();
    return () => animation.stop();
  }, [progress, reducedMotion, message]);

  return (
    <Animated.View pointerEvents="none" accessibilityLiveRegion="polite" style={[styles.toast, { backgroundColor: tone === 'success' ? Colors.dark : palette.background }, style, { opacity: progress, transform: [{ translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [18, 0] }) }] }]}>
      <Ionicons name={palette.icon} size={16} color={tone === 'success' ? '#FFFFFF' : palette.color} />
      <Text style={[styles.toastText, tone !== 'success' && { color: palette.color }]}>{message}</Text>
    </Animated.View>
  );
}

export function InlineNotice({ title, message, onAction, actionLabel = 'Tentar novamente' }: { title: string; message: string; onAction?: () => void; actionLabel?: string }) {
  const palette = TONE.error;
  return (
    <View accessibilityLiveRegion="assertive" style={styles.notice}>
      <View style={[styles.noticeIcon, { backgroundColor: palette.background }]}><Ionicons name={palette.icon} size={17} color={palette.color} /></View>
      <View style={styles.noticeCopy}>
        <Text style={styles.noticeTitle}>{title}</Text>
        <Text style={styles.noticeMessage}>{message}</Text>
      </View>
      {onAction && <PressableScale onPress={onAction} accessibilityLabel={actionLabel} containerStyle={styles.noticeActionPress} contentStyle={styles.noticeAction}><Text style={styles.noticeActionText}>{actionLabel}</Text></PressableScale>}
    </View>
  );
}

export function OfflineBanner({ lastSyncedAt }: { lastSyncedAt?: string | null }) {
  const synced = lastSyncedAt ? new Date(lastSyncedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : null;
  return (
    <View accessibilityRole="alert" accessibilityLiveRegion="polite" style={styles.offlineBanner}>
      <View style={styles.offlineIcon}><Ionicons name="cloud-offline-outline" size={16} color={Colors.warningDk} /></View>
      <View style={styles.offlineCopy}>
        <Text style={styles.offlineTitle}>Sem conexão</Text>
        <Text style={styles.offlineMessage}>{synced ? `Exibindo dados salvos às ${synced}.` : 'Exibindo os dados disponíveis no dispositivo.'}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  toast: { position: 'absolute', left: 24, right: 24, bottom: 92, minHeight: 44, paddingHorizontal: 14, borderRadius: 20, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, shadowColor: Colors.dark, shadowOffset: { width: 0, height: 5 }, shadowOpacity: 0.18, shadowRadius: 14, elevation: 8, zIndex: 80 },
  toastText: { color: '#FFFFFF', fontSize: 13, fontWeight: '600', flexShrink: 1 },
  notice: { flexDirection: 'row', alignItems: 'center', gap: 9, padding: 11, borderRadius: 14, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#F2C4CC' },
  noticeIcon: { width: 31, height: 31, borderRadius: 15, alignItems: 'center', justifyContent: 'center' },
  noticeCopy: { flex: 1, minWidth: 0, gap: 2 },
  noticeTitle: { color: Colors.text1, fontSize: 13, fontWeight: '800' },
  noticeMessage: { color: Colors.text2, fontSize: 12, lineHeight: 16 },
  noticeActionPress: { borderRadius: 99 },
  noticeAction: { minHeight: 32, justifyContent: 'center', paddingHorizontal: 11, borderRadius: 99, backgroundColor: Colors.primaryLt },
  noticeActionText: { color: Colors.primary, fontSize: 12, fontWeight: '800' },
  offlineBanner: { position: 'absolute', zIndex: 100, top: Platform.OS === 'web' ? ('calc(var(--ced-safe-top, 0px) + 8px)' as any) : 8, left: 12, right: 12, minHeight: 45, paddingHorizontal: 11, paddingVertical: 7, borderRadius: 14, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#FDF6E8', borderWidth: 1, borderColor: '#EFD39C', shadowColor: Colors.dark, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.12, shadowRadius: 12, elevation: 5 },
  offlineIcon: { width: 29, height: 29, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.warningLt },
  offlineCopy: { flex: 1, gap: 1 },
  offlineTitle: { color: Colors.text1, fontSize: 13, fontWeight: '800' },
  offlineMessage: { color: Colors.text2, fontSize: 12, lineHeight: 16 },
});
